import {
  Autocomplete,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getSearchOrder } from "../const";

import IconButton from "@common/components/IconButton";
import { FiltersTypeImpl } from "@common/utils/filters";
import { Clear, Info } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  cloudConnectors,
  consumptions,
} from "@pages/Assets/Accounts/AccountDrawer/Content/context/fieldValue";
import { roomTypes } from "@pages/Assets/Rooms/RoomDrawer/Content/context/fieldValue";
import {
  accountsStatusFilter,
  assignedToFilter,
  auditTimeFilter,
  countriesStatusFilter,
  devicesServiceFilter,
  devicesSourceFilter,
  devicesStatusFilter,
  locationsStatusFilter,
  meetingsAssigneeFilter,
  meetingsStatusFilter,
  meetingsTimeFilter,
  roomsStatusFilter,
  roomsServiceExpirationTimeFilter,
  ticketsPurposeFilter,
  ticketsStateFilter,
  ticketsTimeFilter,
  workflowsTypeFilter,
  devicesWarrantyExpirationTimeFilter,
  devicesLeaseExpirationTimeFilter,
  ticketsPriorityFilter,
} from "@pages/Dashboard/WidgetEdit/FilterPanel/constants";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import {
  deviceOperatingSystems,
  deviceUCPlatforms,
} from "@services/devices/types";
import { Assignee } from "@services/meetings/assignee";
import {
  MeetingPurpose,
  MeetingPurposeLabels,
  MeetingSource,
  MeetingType,
  MeetingTypeLabels,
  ScheduledMeetingProducerService,
  ScheduledMeetingProducerServiceLabels,
} from "@services/meetings/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { Role } from "@services/user/types";
import { useTranslation } from "react-i18next";
import {
  DeviceFieldContextType,
  FieldValueContext,
} from "../../context/fieldValue";
import { RoleContext } from "../../context/roles";
import { SearchContext } from "../../context/search";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { displayCountryName, sfcountries } from "@assets/countries";
import {
  defaultEntityTypeFilter,
  EntityTypeList,
  getAvailableActions,
} from "@pages/BI/Audit/const";
import {
  devicesSourcesFilteredByLicense,
  getFilteredDeviceSourceValues,
} from "@common/utils/devices/validation";
import { getTestId } from "@common/utils/getTestId";
import { extendedMUIInputLabelProps } from "@common/utils/extendedMUIInputLabelProps";
import { MonitoringSourceValueMapping } from "@common/utils/devices/monitoringSource";

// This is the default size that browser attributes to inputs, when no size/width is specified
// Need to have it hard-coded to force this size even when there's an endAdornment
const DEFAULT_SIZE = "30ch";
const END_ADORNMENT_SIZE = 80;
interface ValueObject<X extends string | number> {
  key: X;
  label: string;
}
type ValueType<X extends string = string> = X | ValueObject<X>;
type ValueArray = Array<ValueType>;
interface PropsType {
  label?: string;
  name?: string;
  value?: string;
  fullWidth?: boolean;
  margin?: TextFieldProps["margin"];
  onChange?: (event: {
    target: {
      value: string;
      name?: string;
    };
  }) => void;
  errorText?: string;
  disabled?: boolean;
  testId?: string;
}
const getLabelSx = (value?: string): SxProps =>
  (value ?? "").length === 0
    ? {
        "&:not(.Mui-focused)": {
          transform: `translate(14px, 0.5em) scale(1)`,
        },
      }
    : {};
const roleToValueObjectMapper = (role: Role) => ({
  key: `${role.id}`,
  label: role.name ?? "",
});
export const EndAdornment = ({
  errorText,
  sx,
  selectAdornment = false,
}: {
  errorText?: string;
  sx?: SxProps;
  selectAdornment?: boolean;
}) =>
  errorText === undefined ? (
    <></>
  ) : (
    <InputAdornment
      position="end"
      sx={[
        { cursor: "default", marginRight: selectAdornment ? 2 : 0 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Tooltip arrow title={errorText ?? ""}>
        <Info color="error" />
      </Tooltip>
    </InputAdornment>
  );
export const SelectField = (
  props: PropsType &
    (
      | {
          values: ValueArray;
          source?: undefined;
        }
      | {
          values?: undefined | [];
          source: keyof DeviceFieldContextType | "roles";
        }
    ) & {
      handleClickSelectClear?: () => void;
      hideIconButton?: boolean;
      allField?: boolean;
      tooltip?: boolean;
    }
) => {
  const {
    label,
    name,
    value = "",
    values = [],
    source,
    fullWidth,
    margin,
    onChange = () => {},
    errorText,
    disabled = false,
    allField = false,
    tooltip = false,
    testId,
  } = props;

  const context = useContext(FieldValueContext);
  const ref = useRef<HTMLDivElement>(null);
  const { roles } = useContext(RoleContext);
  const [open, setOpen] = useState(false);
  const selectFieldValues =
    source !== undefined
      ? source === "roles"
        ? roles.map(roleToValueObjectMapper)
        : context[source]
      : values;
  const showClearButton =
    !props.hideIconButton && Boolean(value) && props.handleClickSelectClear;

  useEffect(() => {
    if (
      value !== "" &&
      selectFieldValues.length > 0 &&
      selectFieldValues
        .map((text) =>
          typeof text === "string" ? { key: text, label: text } : text
        )
        .every(({ key }) => key !== value)
    )
      onChange({ target: { value: "" } });
  }, []);
  return (
    <FormControl
      margin={margin}
      ref={ref}
      error={Boolean(errorText)}
      sx={(theme) => ({
        marginRight: fullWidth ? undefined : theme.spacing(1),
        cursor: "pointer",
        "& .MuiInputBase-root": fullWidth
          ? {}
          : {
              minWidth: DEFAULT_SIZE,
              width: DEFAULT_SIZE,
              maxWidth: DEFAULT_SIZE,
            },
      })}
    >
      <InputLabel
        sx={getLabelSx(value)}
        data-testid={testId && `${testId}-label`}
      >
        {label}
      </InputLabel>

      <Select
        name={name}
        value={value}
        disabled={disabled || selectFieldValues.length === 0}
        onChange={(evt) => {
          onChange(evt);
          setOpen(false);
        }}
        onOpen={() => setOpen(true)}
        fullWidth={fullWidth}
        label={label}
        open={open}
        sx={(theme) => ({
          borderRadius: "8px",
          borderColor: theme.palette.common.grey.light,
          cursor: "pointer",
          "& .MuiSelect-select": {
            "& > span": {
              textOverflow: "ellipsis",
              overflow: "hidden",
              width: "100%",
            },
          },
        })}
        autoWidth
        endAdornment={
          Boolean(errorText) ? (
            <EndAdornment
              selectAdornment
              errorText={errorText}
              sx={{ cursor: "pointer" }}
            />
          ) : showClearButton ? (
            <InputAdornment
              position="end"
              sx={{
                position: "absolute",
                right: "30px",
              }}
            >
              <IconButton
                sx={{
                  color: ({ palette }) => palette.common.white,
                }}
                onClick={() =>
                  props.handleClickSelectClear && props.handleClickSelectClear()
                }
              >
                <Clear />
              </IconButton>
            </InputAdornment>
          ) : (
            <></>
          )
        }
        MenuProps={{
          anchorEl: ref.current,
          onClose: () => {
            setOpen(false);
          },
          slotProps: {
            paper: {
              sx: { width: ref.current?.getBoundingClientRect().width },
            },
          },
        }}
        data-testid={testId && `${testId}-select`}
      >
        {selectFieldValues
          .map((text) => {
            const { key, label } =
              typeof text === "string" ? { key: text, label: text } : text;
            return { key, label };
          })
          .sort((a, b) => (a?.label ?? "").localeCompare(b?.label ?? ""))
          .map(({ key, label }) => {
            return (
              <MenuItem value={key} key={key}>
                {tooltip ? (
                  <Tooltip title={label ?? ""} arrow>
                    <Typography
                      noWrap
                      sx={{
                        marginRight:
                          showClearButton && value === key ? "1rem" : "",
                        width:
                          ref.current && open
                            ? ref.current?.getBoundingClientRect().width
                            : ref.current && fullWidth
                            ? ref.current?.getBoundingClientRect().width -
                              END_ADORNMENT_SIZE
                            : "auto",
                      }}
                    >
                      {label}
                    </Typography>
                  </Tooltip>
                ) : (
                  label
                )}
              </MenuItem>
            );
          })}
      </Select>
    </FormControl>
  );
};
const AutocompleteField = (
  props: Partial<PropsType> &
    (
      | {
          values: { key: string; label: string }[];
          source?: undefined;
        }
      | {
          values?: undefined | [];
          source: keyof DeviceFieldContextType;
        }
    ) & {
      getAutoCompleteValues?: (key: FilterTypes) => string[];
      handleAutoComplete?: (key: FilterTypes, values: string[]) => void;
      property: FilterTypes;
      freeSolo?: boolean;
      placeHolder?: string;
    }
) => {
  const {
    label,
    values = [],
    source,
    fullWidth,
    getAutoCompleteValues,
    handleAutoComplete,
    property,
    freeSolo,
    errorText,

    testId,
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const context = useContext(FieldValueContext);

  const optionValues =
    source !== undefined
      ? context[source].map((value) => {
          return { key: value, label: value };
        })
      : values;

  const selectedValues = useMemo(() => {
    const tmp: { key: string; label: string }[] = [];

    getAutoCompleteValues &&
      getAutoCompleteValues(property).forEach((v) => {
        const value = optionValues.find((value) => value.key === v);
        if (value) {
          tmp.push(value);
        } else if (typeof v === "string") {
          if (freeSolo) {
            tmp.push({ key: v, label: v });
          } else if (property === "source") {
            const label = v === "DMCA" ? v : MonitoringSourceValueMapping[v];
            label && tmp.push({ key: v, label });
          }
        }
      });
    return tmp;
  }, [optionValues]);

  const [inputValue, setInputValue] = useState("");

  return (
    <Autocomplete
      ref={ref}
      freeSolo={freeSolo}
      multiple
      value={selectedValues}
      options={optionValues.sort((a, b) => a.label.localeCompare(b.label))}
      size={"small"}
      sx={(theme) => ({
        ...(fullWidth
          ? {
              marginTop: "8px",
              marginBottom: "4px",
              cursor: "pointer",
              "& .MuiInputBase-root": {
                borderRadius: "8px",
              },
            }
          : {
              minWidth: DEFAULT_SIZE,
              marginRight: theme.spacing(1),
              "& .MuiInputBase-root": {
                borderRadius: "8px",
              },
            }),
        "& .MuiAutocomplete-clearIndicator": {
          visibility: "visible",
          marginRight: "0px",
        },
      })}
      fullWidth={fullWidth}
      inputValue={inputValue}
      onInputChange={(e, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={() => {
        if (inputValue.trim() !== "") {
          const isAlreadySelected = selectedValues.some(
            (value) => value.key === inputValue
          );

          if (!isAlreadySelected) {
            const newValues = [...selectedValues.map((v) => v.key), inputValue];
            handleAutoComplete && handleAutoComplete(property, newValues);
          }

          setInputValue("");
        }
      }}
      onChange={(event, values) => {
        let tmp: string[] = [];
        values.forEach((value) => {
          var found = tmp.find((item) =>
            typeof value === "string" ? item === value : item === value.key
          );
          if (!found) {
            typeof value === "string" ? tmp.push(value) : tmp.push(value.key);
          }
        });
        handleAutoComplete && handleAutoComplete(property, tmp);
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Tooltip
              key={key}
              title={typeof option === "string" ? option : option.label}
              arrow
            >
              <Chip
                {...tagProps}
                size={"small"}
                label={typeof option === "string" ? option : option.label}
              />
            </Tooltip>
          );
        })
      }
      clearIcon={<Clear />}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={props.placeHolder}
          error={Boolean(errorText)}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {Boolean(errorText) ? (
                    <>
                      <EndAdornment
                        selectAdornment
                        errorText={errorText}
                        sx={{ cursor: "pointer" }}
                      />
                      {params.InputProps.endAdornment}
                    </>
                  ) : (
                    params.InputProps.endAdornment
                  )}
                </>
              ),
            },

            htmlInput: {
              ...params.inputProps,
              "data-testid": testId && `${testId}-input`,
            },

            inputLabel: extendedMUIInputLabelProps({
              ...params.InputLabelProps,
              "data-testid": testId && `${testId}-label`,
            }),
          }}
        />
      )}
    />
  );
};
export const StyledTextField = styled(TextField)(({ theme }) => ({
  marginRight: theme.spacing(1),
  "& .MuiInputBase-root": {
    borderRadius: "8px",
    borderColor: theme.palette.common.grey.light,
  },
}));

const AssignedField = (props: SearchFieldProps) => {
  const {
    value,
    onEnterKey = () => {},
    errorText,
    handleClickSelectClear,
    property,
    entity,
    testId,
    ...other
  } = props;
  const { t } = useTranslation();
  const [user] = useAuthenticatedUser();

  const isAssignedToSelectValue = useMemo(
    () => assignedToFilter.some((item) => item.value === value),
    [value]
  );

  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    isAssignedToSelectValue ? value : Boolean(value) ? "Assigned to" : undefined
  );

  const [textFieldValue, setTextFieldValue] = useState<string | undefined>(
    isAssignedToSelectValue ? undefined : value
  );
  const handleChange = (event: {
    target: {
      value: string;
      name?: string;
    };
  }) => {
    const { name, value } = event.target;

    if (name === "select") {
      setSelectedValue(value);
      other.onChange({
        target: {
          value: value !== "Assigned to" ? value : "",
        },
      });
    } else if (name === "textfield") {
      setTextFieldValue(value);
      other.onChange({
        target: {
          value: value,
        },
      });
    }
  };

  const clearValue = () => {
    setTextFieldValue(undefined);
    setSelectedValue(undefined);
    handleClickSelectClear && handleClickSelectClear(property);
  };

  const getAssignedValue = (
    isAssignedToSelectValue: boolean,
    value: string | undefined
  ): string | undefined => {
    const hasAssignedTo = entity ? "assignedTo" in entity : true;
    if (!hasAssignedTo) return undefined;
    if (isAssignedToSelectValue || value === user?.email) return value;
    return value ? "Assigned to" : selectedValue;
  };

  useEffect(() => {
    setTextFieldValue("");
    if (!value && selectedValue && selectedValue !== "Assigned to") {
      setSelectedValue(undefined);
      return;
    }

    const assignedValue = getAssignedValue(isAssignedToSelectValue, value);
    setSelectedValue(assignedValue);
  }, [entity, selectedValue, value]);

  return (
    <>
      <SelectField
        allField
        value={Boolean(selectedValue) ? selectedValue : undefined}
        values={assignedToFilter.map(({ label, value }) => ({
          key: value === "Assigned to Me" ? user?.email ?? "" : value,
          label: label,
        }))}
        {...other}
        name="select"
        onChange={handleChange}
        label={t(other.label as string)}
        handleClickSelectClear={clearValue}
        testId={testId}
      />

      {selectedValue === "Assigned to" && (
        <StyledTextField
          onKeyUp={({ key }) => {
            if (key === "Enter") onEnterKey && onEnterKey();
          }}
          slotProps={{
            htmlInput: {
              "data-testid": "assigned-to-email-input",
            },
            inputLabel: extendedMUIInputLabelProps({
              sx: getLabelSx(value),
              "data-testid": "assigned-to-email-label",
            }),
          }}
          value={Boolean(textFieldValue) ? textFieldValue : value}
          {...other}
          name="textfield"
          onChange={handleChange}
          label={t("Assigned to email")}
          error={Boolean(errorText)}
          sx={Array.isArray(other.sx) ? [...other.sx] : [other.sx]}
        />
      )}
    </>
  );
};

export const SearchTypeToggle = (props: {
  selected: SearchType;
  onChange: (value: SearchType) => void;
}) => {
  const [user] = useAuthenticatedUser();
  const { selected, onChange } = props;
  const { t } = useTranslation();
  return (
    <Select
      value={selected}
      onChange={(evt) => onChange(evt.target.value as SearchType)}
      sx={{ width: "20ch" }}
      data-testid="search-type-select"
    >
      {getSearchOrder(user).map((s) => (
        <MenuItem key={s} value={s} onClick={() => onChange(s)}>
          {t(s)}
        </MenuItem>
      ))}
    </Select>
  );
};

type SelectKeys =
  | "deviceType"
  | "device.type"
  | "category"
  | "device.category"
  | "manufacturer"
  | "device.manufacturer"
  | "room.location.country"
  | "location.country"
  | "country"
  | "role.id"
  | "threshold.deviceType"
  | "threshold.category"
  | "threshold.manufacturer";

type SearchFieldSelect = { property: SelectKeys } & PropsType;
type SearchFieldProps = TextFieldProps &
  (({ property: string } & Partial<PropsType>) | SearchFieldSelect) & {
    onChange: (evt: { target: { value: string } }) => void;
    onEnterKey?: () => void;
    filterType?: FiltersType;
    getAutoCompleteValues?: (key: FilterTypes) => string[];
    handleAutoComplete?: (key: FilterTypes, values: string[]) => void;
    handleClickSelectClear?: (key: string) => void;
    hideIconButton?: boolean;
    getProducersList?: () => Promise<void>;
    producersList?: Assignee[];
    isAssignedTo?: boolean;
    entity?: any;
    entityType?: EntityTypeList | EntityTypeList[];
    updateMonitoringSourcesOnAccountChange?: boolean;
  };

export const SearchField = (props: SearchFieldProps) => {
  const {
    filterType,
    property,
    value,
    onEnterKey = () => {},
    errorText,
    getAutoCompleteValues,
    handleAutoComplete,
    handleClickSelectClear,
    getProducersList,
    producersList,
    hideIconButton,
    isAssignedTo,
    entity,
    entityType,
    updateMonitoringSourcesOnAccountChange = true,
    ...other
  } = props;

  const { searchType } = useContext(SearchContext);
  const { t } = useTranslation();
  const [accounts, selectAll, allAccounts] = useUserSelectedAccounts();
  const [user] = useAuthenticatedUser();

  const equival: {
    [k in SelectKeys]: keyof DeviceFieldContextType | "roles";
  } = {
    "room.location.country": "countries",
    country: "countries",
    "location.country": "countries",
    category: "categories",
    "device.category": "categories",
    deviceType: "deviceTypes",
    "device.type": "deviceTypes",
    manufacturer: "manufacturers",
    "device.manufacturer": "manufacturers",
    "role.id": "roles",
    "threshold.deviceType": "deviceTypes",
    "threshold.category": "categories",
    "threshold.manufacturer": "manufacturers",
  };
  const booleanSelect: ValueArray = [
    { label: t("Yes"), key: "true" },
    { label: t("No"), key: "false" },
  ];
  const notBooleanSelect: ValueArray = [
    { label: t("Yes"), key: "false" },
    { label: t("No"), key: "true" },
  ];
  const openCloseSelect: ValueArray = [
    { label: t("Open"), key: "true" },
    { label: t("Closed"), key: "false" },
  ];
  const isError = Boolean(errorText);
  const sx = other.fullWidth
    ? {}
    : {
        minWidth: DEFAULT_SIZE,
        width: DEFAULT_SIZE,
        maxWidth: DEFAULT_SIZE,
      };

  const testIdRoot = getTestId(other.label ?? property);
  const inputTestId = `${testIdRoot}-input`;
  const labelTestId = `${testIdRoot}-label`;

  switch (property) {
    case "provisioned":
      return (
        <SelectField
          value={value}
          values={notBooleanSelect}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "hideNotApplicable":
    case "threshold.active":
    case "threshold.workflow":
    case "includeAllWithOpenTickets":
    case "room.managed":
    case "inService":
    case "isAggregator":
    case "active":
    case "details.publicRoom":
    case "managed":
    case "details.certificationStatus":
    case "wrappedUp":
    case "vip":
    case "inACall":
    case "callCapable":
    case "author":
      return (
        <SelectField
          value={value}
          values={booleanSelect}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "syncStatus":
      return (
        <SelectField
          value={value}
          values={[
            { label: t("Success"), key: "Success" },
            { label: t("Error"), key: "Error" },
          ]}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "threshold.deviceType":
    case "threshold.manufacturer":
    case "threshold.category":
    case "device.type":
    case "category":
    case "device.category":
    case "manufacturer":
    case "device.manufacturer":
    case "room.location.country":
    case "role.id":
      return (
        <SelectField
          source={equival[property]}
          value={value}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          tooltip
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "deviceType":
      return (
        <AutocompleteField
          property="deviceType"
          source={equival[property] as keyof DeviceFieldContextType}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          fullWidth={false}
          errorText={errorText}
          {...other}
          testId={testIdRoot}
        />
      );
    case "entityType":
      return (
        <AutocompleteField
          property="type"
          values={defaultEntityTypeFilter.map(({ type, label }) => ({
            key: type,
            label,
          }))}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          fullWidth={false}
          errorText={errorText}
          {...other}
        />
      );
    case "action":
      return (
        <AutocompleteField
          property="action"
          values={getAvailableActions(entityType ?? []).map(
            ({ type, label }) => ({
              key: type,
              label,
            })
          )}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          fullWidth={false}
          errorText={errorText}
          {...other}
        />
      );
    case "location.country":
    case "country":
      return (
        <AutocompleteField
          property={property as FilterTypes}
          values={sfcountries
            .sort((a, b) => a.alpha2.localeCompare(b.alpha2))
            .map((value) => ({
              key: value.alpha2,
              label: displayCountryName(value, t),
            }))}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          fullWidth={false}
          errorText={errorText}
          {...other}
          testId={testIdRoot}
        />
      );
    case "threshold.priority":
    case "priority": {
      return (
        <AutocompleteField
          property={"priority"}
          values={ticketsPriorityFilter
            .filter((f) =>
              property === "threshold.priority"
                ? f.value !== "Informational"
                : true
            )
            .map((value) => ({
              key: value.value,
              label: value.label,
            }))}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          fullWidth={false}
          errorText={errorText}
          {...other}
          testId={testIdRoot}
        />
      );
    }
    case "cloudConnector":
      return (
        <SelectField
          value={value}
          values={cloudConnectors.map(({ id, name }) => ({
            key: id,
            label: name,
          }))}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
        />
      );
    case "consumption":
      return (
        <SelectField
          value={value}
          values={consumptions.map(({ id, name }) => ({
            key: id,
            label: name,
          }))}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
        />
      );
    case "ucPlatform":
      return (
        <SelectField
          value={value}
          values={deviceUCPlatforms.map(({ label, value }) => ({
            label,
            key: value,
          }))}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "operatingSystem":
      return (
        <SelectField
          value={value}
          values={deviceOperatingSystems.map(({ label, value }) => ({
            label,
            key: value,
          }))}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "end.before":
    case "start.after":
    case "lastUpdatedDate":
    case "details.serviceExpirationDate.after":
    case "details.serviceExpirationDate.before":
    case "details.leaseExpirationDate.after":
    case "details.leaseExpirationDate.before":
    case "details.warrantyExpirationDate.after":
    case "details.warrantyExpirationDate.before":
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            slots={{ textField: StyledTextField }}
            slotProps={{
              textField: (params) => ({
                error: isError,
                inputProps: { "data-testid": inputTestId },
                InputProps: {
                  endAdornment: (
                    <>
                      <EndAdornment errorText={errorText} />
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                },
                InputLabelProps: {
                  shrink: true,
                  sx: getLabelSx(value),
                  "data-testid": labelTestId,
                },
              }),
            }}
            onChange={(date: Date | null) => {
              other.onChange({
                target: {
                  value: `${date?.getTime()}`,
                },
              });
            }}
            onOpen={() => {}}
            value={value !== undefined ? new Date(parseInt(value)) : null}
          />
        </LocalizationProvider>
      );
    case "service":
      return (
        <AutocompleteField
          property={property}
          values={[
            ...devicesServiceFilter.map(({ value, label }) => ({
              key: value,
              label: label,
            })),
          ]}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          {...other}
          errorText={errorText}
          testId={testIdRoot}
        />
      );
    case "source":
      const monitoringSources = devicesSourcesFilteredByLicense(
        devicesSourceFilter,
        allAccounts.find((allAccount) => {
          const accountId = entity?.["accountId"] ?? user?.account.id;
          return accountId === allAccount.id;
        })
      );

      const monitoringSourcesOptions = monitoringSources.map(
        ({ value, label }) => ({
          key: value,
          label: label,
        })
      );

      return (
        <AutocompleteField
          property={property}
          values={monitoringSourcesOptions}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          {...other}
          errorText={errorText}
          testId={testIdRoot}
        />
      );
    case "accountId":
      const unknownOption = { key: "Unknown", label: t("Unknown") };
      const isValueUnknown = value && !accounts.includes(value);
      const sourceValue: string[] = entity?.["source"] ??
        entity?.["monitoringSources"] ?? [""];

      const correctDeviceSourceValue = (accountId: string) => {
        if (!accountId) return;

        const selectedAccount = allAccounts.find(
          (account) => accountId === account.id
        );
        if (!selectedAccount) return;

        const deviceSourceValues = getFilteredDeviceSourceValues(
          selectedAccount,
          devicesSourceFilter,
          sourceValue
        );

        if (
          updateMonitoringSourcesOnAccountChange &&
          handleAutoComplete &&
          deviceSourceValues.length > 0
        ) {
          handleAutoComplete("source", deviceSourceValues);
        }
      };

      return (
        <SelectField
          allField
          tooltip
          value={
            isValueUnknown
              ? unknownOption.key
              : accounts.length === 1
              ? accounts[0]
              : value
          }
          values={[
            ...(isValueUnknown ? [unknownOption] : []),
            ...allAccounts
              .filter((x) => accounts.includes(x.id))
              .map(({ id, name }) => ({
                key: id,
                label: name ?? "",
              })),
          ]}
          onChange={(evt) => {
            other.onChange({
              target: {
                value: evt.target.value,
              },
            }),
              correctDeviceSourceValue(evt.target.value);
          }}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          fullWidth={other.fullWidth}
          margin={other.margin}
          hideIconButton={hideIconButton ?? accounts.length === 1}
          testId={testIdRoot}
        />
      );
    case "details.roomType":
    case "room.details.roomType":
      return (
        <AutocompleteField
          freeSolo
          property={property}
          values={[
            ...roomTypes.map(({ id, name }) => ({
              key: id,
              label: name,
            })),
          ]}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          {...other}
          errorText={errorText}
          placeHolder={"Add Custom Room Type..."}
          testId={testIdRoot}
        />
      );

    case "tags":
      return (
        <AutocompleteField
          {...other}
          freeSolo
          values={[]}
          property={property}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          errorText={errorText}
          testId={testIdRoot}
        />
      );
    case "assignee":
      return (
        <SelectField
          value={value}
          values={meetingsAssigneeFilter.map(({ label, value }) => ({
            key: value,
            label: label,
          }))}
          onChange={(evt) => {
            other.onChange({
              target: {
                value: evt.target.value,
              },
            });
            if (evt.target.value === "ASSIGNED_TO") {
              getProducersList && getProducersList();
            }
          }}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          fullWidth={other.fullWidth}
          margin={other.margin}
          testId={testIdRoot}
        />
      );
    case "producerIds":
      return (
        <AutocompleteField
          property={property}
          values={(producersList ?? []).map(({ id, firstName, lastName }) => ({
            key: id,
            label: `${firstName} ${lastName}`,
          }))}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          errorText={errorText}
          {...other}
          testId={testIdRoot}
        />
      );

    case "scheduledMeeting.meetingSource":
      return (
        <AutocompleteField
          property={property}
          values={[
            ...Object.entries(MeetingSource).map(([value, label]) => ({
              key: value,
              label: label,
            })),
          ]}
          label={t(other.label as string)}
          handleAutoComplete={handleAutoComplete}
          getAutoCompleteValues={getAutoCompleteValues}
          {...other}
          errorText={errorText}
          testId={testIdRoot}
        />
      );
    case "type":
      switch (filterType ?? searchType) {
        case FiltersTypeImpl.Meetings:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...Object.entries(MeetingType).map(([value, label]) => ({
                  key: value,
                  label: MeetingTypeLabels[label],
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              errorText={errorText}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Workflows:
          return (
            <AutocompleteField
              property={property}
              values={workflowsTypeFilter.map(({ value, label }) => ({
                key: value,
                label: label,
              }))}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              errorText={errorText}
              {...other}
            />
          );
      }
    case "purpose":
      switch (filterType ?? searchType) {
        case FiltersTypeImpl.Meetings:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...Object.entries(MeetingPurpose).map(([value, label]) => ({
                  key: value,
                  label: MeetingPurposeLabels[label],
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              errorText={errorText}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Tickets:
          return (
            <SelectField
              allField
              value={Boolean(value) ? value : undefined}
              values={ticketsPurposeFilter.map(({ label, value }) => ({
                key: value,
                label: label,
              }))}
              {...other}
              label={t(other.label as string)}
              errorText={errorText}
              handleClickSelectClear={() =>
                handleClickSelectClear && handleClickSelectClear(property)
              }
              testId={testIdRoot}
            />
          );
        default:
          break;
      }
    case "scheduledMeeting.producerService":
      return (
        <SelectField
          value={value}
          values={[
            ...Object.entries(ScheduledMeetingProducerService).map(
              ([value, label]) => ({
                key: value,
                label: ScheduledMeetingProducerServiceLabels[label],
              })
            ),
          ]}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "assignedTo":
      return (
        <AssignedField
          value={value}
          onEnterKey={onEnterKey}
          errorText={errorText}
          handleClickSelectClear={handleClickSelectClear}
          property={property}
          sx={other.sx}
          entity={entity}
          {...other}
          testId={testIdRoot}
        />
      );
    case "time":
      switch (filterType ?? searchType) {
        case FiltersTypeImpl.Meetings:
          return (
            <SelectField
              value={value}
              values={meetingsTimeFilter.map(({ label, value }) => ({
                key: value,
                label: label,
              }))}
              {...other}
              onChange={(evt) =>
                other.onChange({
                  target: {
                    value: evt.target.value,
                  },
                })
              }
              label={t(other.label as string)}
              errorText={errorText}
              handleClickSelectClear={() =>
                handleClickSelectClear && handleClickSelectClear(property)
              }
              hideIconButton={value === "For the next 12 hours"}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Tickets:
          return (
            <SelectField
              value={value}
              values={ticketsTimeFilter.map(({ label, value }) => ({
                key: value,
                label: label,
              }))}
              {...other}
              onChange={(evt) =>
                other.onChange({
                  target: {
                    value: evt.target.value,
                  },
                })
              }
              label={t(other.label as string)}
              errorText={errorText}
              handleClickSelectClear={() =>
                handleClickSelectClear && handleClickSelectClear(property)
              }
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.AuditTrails:
          return (
            <SelectField
              value={value}
              values={auditTimeFilter.map(({ label, value }) => ({
                key: value,
                label: label,
              }))}
              {...other}
              label={t(other.label as string)}
              errorText={errorText}
              handleClickSelectClear={() =>
                handleClickSelectClear && handleClickSelectClear(property)
              }
              testId={testIdRoot}
            />
          );
        default:
          break;
      }
    case "timeServiceExpiration":
      return (
        <SelectField
          value={value}
          values={roomsServiceExpirationTimeFilter.map(({ label, value }) => ({
            key: value,
            label: label,
          }))}
          {...other}
          onChange={(evt) =>
            other.onChange({
              target: {
                value: evt.target.value,
              },
            })
          }
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "timeLeaseExpiration":
    case "timeWarrantyExpiration":
      return (
        <SelectField
          value={value}
          values={(other.label === "Warranty Expiration"
            ? devicesWarrantyExpirationTimeFilter
            : devicesLeaseExpirationTimeFilter
          ).map(({ label, value }) => ({
            key: value,
            label: label,
          }))}
          {...other}
          onChange={(evt) =>
            other.onChange({
              target: {
                value: evt.target.value,
              },
            })
          }
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    case "location.open":
    case "open":
      return (
        <SelectField
          value={value}
          values={openCloseSelect}
          {...other}
          label={t(other.label as string)}
          errorText={errorText}
          handleClickSelectClear={() =>
            handleClickSelectClear && handleClickSelectClear(property)
          }
          testId={testIdRoot}
        />
      );
    /**
     * Cascading down from there into the default when condition isn't matched
     */
    case "status":
      switch (filterType ?? searchType) {
        case FiltersTypeImpl.Tickets:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...ticketsStateFilter.map(({ value, label }) => ({
                  key: value,
                  label: label,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              fullWidth={false}
              errorText={errorText}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Meetings:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...meetingsStatusFilter.map(({ value, label }) => ({
                  label,
                  key: value,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              fullWidth={false}
              errorText={errorText}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Devices:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...devicesStatusFilter.map((status) => ({
                  key: status.value,
                  label: status.label,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              fullWidth={false}
              errorText={errorText}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Accounts:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...accountsStatusFilter.map((status) => ({
                  key: status.value,
                  label: status.label,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              errorText={errorText}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Rooms:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...roomsStatusFilter.map((status) => ({
                  key: status.value,
                  label: status.label,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Locations:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...locationsStatusFilter.map((status) => ({
                  key: status.value,
                  label: status.label,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              {...other}
              testId={testIdRoot}
            />
          );
        case FiltersTypeImpl.Countries:
          return (
            <AutocompleteField
              property={property}
              values={[
                ...countriesStatusFilter.map((status) => ({
                  key: status.value,
                  label: status.label,
                })),
              ]}
              label={t(other.label as string)}
              handleAutoComplete={handleAutoComplete}
              getAutoCompleteValues={getAutoCompleteValues}
              {...other}
              testId={testIdRoot}
            />
          );
        default:
          break;
      }

    default:
      return (
        <StyledTextField
          onKeyUp={({ key }) => {
            if (key === "Enter") onEnterKey();
          }}
          slotProps={{
            htmlInput: {
              "data-testid": inputTestId,
            },
            inputLabel: extendedMUIInputLabelProps({
              sx: getLabelSx(value),
              "data-testid": labelTestId,
            }),
            input: {
              ...(other.InputProps ?? {}),
              endAdornment: (
                <>
                  {Boolean(errorText) ? (
                    <>
                      <EndAdornment
                        selectAdornment
                        errorText={errorText}
                        sx={{ cursor: "pointer" }}
                      />
                      {other.InputProps?.endAdornment}
                    </>
                  ) : !props.hideIconButton &&
                    Boolean(value) &&
                    props.handleClickSelectClear ? (
                    <InputAdornment
                      position="end"
                      sx={{ position: "absolute", right: "30px" }}
                    >
                      <IconButton
                        sx={{
                          color: ({ palette }) => palette.common.white,
                        }}
                        onClick={() =>
                          props.handleClickSelectClear &&
                          props.handleClickSelectClear(property)
                        }
                      >
                        <Clear />
                      </IconButton>
                    </InputAdornment>
                  ) : null}
                </>
              ),
            },
          }}
          value={value}
          {...other}
          label={t(other.label as string)}
          error={isError}
          sx={Array.isArray(other.sx) ? [...other.sx, sx] : [other.sx, sx]}
        />
      );
  }
};

export default SelectField;
